
import Vue from 'vue';
import { Component, Provide } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import AdUnit from '@/components/AdUnit.vue';
import Breadcrumbs from '@/components/sports/common/Breadcrumbs.vue';
import MultiStepEventEncounterList from '@/components/sports/common/MultiStepEventEncounterList.vue';
import Header from '@/components/sports/common/Header.vue';
import CompetitionEventsCalendarOld from '@/components/sports/common/overview/CompetitionEventsCalendarOld.vue';
import ProviderStreamsComparator from '@/components/sports/common/provider-streams-comparator/ProviderStreamsComparator.vue';
import SportsLayout from '@/components/sports/common/SportsLayout.vue';
import Subheading from '@/components/sports/common/Subheading.vue';

import { isSameDate, shiftDateWithTimezone } from '@/helpers/date-helper';
import { getOverviewUrlBySport } from '@/routing/sports';

import { Platform, SportCompetitionV2, SportEventAggregation, SportType } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';

import type { SportEventBucketsEdgeV2 } from '@/@types/graphql-types';
import type { SportLocale } from '@/enums/sports';
import type { WebLocale } from '@/enums/web-locale';
import { LocaleResponse } from '@/helpers/locale-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BreadcrumbItem, SportPageType } from '@/interfaces/sport-graphql';
import {
	GetMultiStepSportOverviewDocument,
	GetMultiStepSportOverviewQueryVariables,
} from '@/pages/graphql/queries/GetMultiStepSportOverview.query';
import type { FilterCollection } from '@/stores/filter.store';

const Language = namespace('language');
const Filter = namespace('filter');
const Routing = namespace('routing');

@Component({
	name: 'MotorsportOverviewPage',
	components: {
		SportsLayout,
		Breadcrumbs,
		Header,
		Subheading,
		CompetitionEventsCalendarOld,
		ProviderStreamsComparator,
		MultiStepEventEncounterList,
		AdUnit,
	},
	apollo: {
		overviewData: {
			query: GetMultiStepSportOverviewDocument,
			variables(): GetMultiStepSportOverviewQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data;
			},
			skip() {
				return (
					this.$route.name !== 'app.sports.overview' ||
					!(this.$route.meta!.data as string)?.includes('multistep')
				);
			},
		},
	},
})
export default class MotorsportOverviewPage extends Vue {
	displayDate = shiftDateWithTimezone(this.$store.getters['language/localeObject'].timezone);
	overviewData: Partial<SportCompetitionV2> = {};
	iconSrc = `/${ASSETS_DIR}/img/sports/icons/sport/formula_racing.png`;

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter localeObject: LocaleResponse;
	@Language.State webLocale: WebLocale;

	@Filter.Getter(CollectionType.SPORTS) filtersSport: FilterCollection;

	@Routing.State urlMetadata: any;

	activated() {
		// note: all sport pages are excluded from automatic page tracking
		/**
		 * setTimeout added here to prevent pageView to be triggered before link click track event.
		 * that's because when adding the @click.native to router-link the track event will get triggered
		 * after the navigation which causes the pageView to get triggered first.
		 *
		 */
		setTimeout(() => {
			TrackingHelper.trackPage(this.$route.path, [this.sportContext]);
		});
	}

	/** Helps support single step components until the multistep refactor. */
	get miniContext() {
		return {
			sportType: SportType.FormulaRacing,
			pageType: SportPageType.SportOverview,
		} as const;
	}

	get sportContext(): SnowplowSportContext {
		return new SnowplowSportContext(this.miniContext.sportType, this.miniContext.pageType);
	}

	get isDataLoading() {
		return this.$apollo.queries.overviewData?.loading;
	}

	get queryVariables(): GetMultiStepSportOverviewQueryVariables {
		return {
			language: this.language,
			country: this.country,
			platform: Platform.Web,
			groupBy: SportEventAggregation.Parent,
			// we need to additionally filter in the frontend for motorsport.
			// that's why we tune up the bucketSize, while keeping `first` low, because there is 'formula 1' as the only competition
			bucketSize: 10, // normally 30, but complexity at 200k
			first: 1,
			filter: {
				startAt: {
					from: this.displayDate.toISOString(),
					to: shiftDateWithTimezone(this.localeObject.timezone, 3, this.displayDate).toISOString(),
				},
				packages: this.filtersSport.providers ?? [],
				sports: [this.miniContext.sportType],
			},
			timezoneOffset: 0,
		};
	}

	get pageInfo() {
		return this.overviewData.sportEventBucketsV2?.pageInfo ?? null;
	}

	get hasLoadMore() {
		return this.pageInfo?.hasNextPage && this.competitions?.length !== 0;
	}

	get competitions() {
		const buckets = this.overviewData.sportEventBucketsV2?.edges ?? [];

		return (
			buckets
				// HACK: currently getting back a wide date range of events,
				// that need to be additionally filtered on the frontend (until gql is ready)
				.map(bucket => {
					return {
						...bucket,
						node: {
							...bucket.node,
							edges: (bucket.node.edges || [])
								// the whole requesting with PARENT mode is a bit funky.
								// formula 1 parent events have multiple days of duration.
								// that's why we filter the child events out in the frontend until the backend follows suit.
								.filter(({ node }) => isSameDate(this.displayDate, new Date(node.content.startAt))),
						},
					};
				})
				// filter out empty competitions/buckets that have no events
				.filter(bucket => (bucket.node.edges || []).length > 0)
		);
	}

	get breadcrumbs(): BreadcrumbItem[] {
		return [
			// e.g. Sport
			{ name: this.$tc('WEBAPP_SPORTS'), routeName: 'app.sports.all' },

			// e.g. Motorsport
			{
				name: this.$tc('SPORTS_WEBAPP_SPORT_TYPE_MOTORSPORT'),
				objectType: SportPageType.SportOverview,
				url: getOverviewUrlBySport(this.miniContext.sportType, this.webLocale as SportLocale),
			},
		];
	}

	handleLoadMore() {
		if (!this.pageInfo?.hasNextPage) return;

		this.$apollo.queries.overviewData?.fetchMore({
			variables: {
				after: this.pageInfo?.endCursor,
			},
			updateQuery(previousResult: SportCompetitionV2, { fetchMoreResult }) {
				const newCompetitions = fetchMoreResult.sportEventBucketsV2.edges;
				const newPageInfo = fetchMoreResult.sportEventBucketsV2.pageInfo;

				return {
					sportEventBucketsV2: {
						__typename: previousResult.sportEventBucketsV2.__typename,
						edges: [...(previousResult.sportEventBucketsV2.edges ?? []), ...newCompetitions],
						pageInfo: newPageInfo,
					},
				};
			},
		});
	}

	@Provide('getEmptyCompetitionHeading')
	getCompetitionHeading(competition: SportEventBucketsEdgeV2) {
		return competition.key.competition?.content.name;
	}
}
